import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';

/**
 * Interface para itens de tabelas auxiliares
 */
interface TabelaAuxiliarItem {
  codigo: number;
  nome: string;
  [key: string]: any;
}

/**
 * Serviço para gerenciamento de tabelas auxiliares do sistema
 */
@Injectable()
export class TabelasAuxiliaresService {
  private readonly urlBase = `${environment.urlAPI}/tabelaauxiliar`;
  private readonly selectOptionFields = ['nome', 'codigo'];

  constructor(private http: HttpClient) { }

  /**
   * Recupera a lista de itens de uma tabela auxiliar
   * @param tabela Nome da tabela a ser recuperada
   * @returns Promise com a lista de itens da tabela
   */
  public getListaTabela(tabela: string): Promise<TabelaAuxiliarItem[]> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('tabela', tabela);

      this.http.get(`${this.urlBase}`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              // Recupera os dados da tabela ou retorna array vazio se não existir
              const data = response.data.tabela || [];
              
              // Aplica sanitização XSS em todos os campos de texto
              resolve(this.sanitizeStringProperties(data));
            } else {
              reject(response.message || `Erro ao buscar tabela: ${tabela}`);
            }
          },
          error: (error) => {
            console.error(`Erro ao buscar tabela:`);
            reject(error);
          }
        });
    });
  }
  
  /**
   * Recupera a lista de itens de uma tabela auxiliar formatados para select
   * @param tabela Nome da tabela a ser recuperada
   * @param valorField Nome do campo a ser usado como valor (default: 'codigo')
   * @param textoField Nome do campo a ser usado como texto (default: 'nome')
   * @returns Promise com a lista de itens formatados para select
   */
  public getListaSelect(tabela: string, valorField: string = 'codigo', textoField: string = 'nome'): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.getListaTabela(tabela)
        .then(data => {
          // Transforma os itens em formato adequado para select
          const selectItems = data.map(item => ({
            id: item[valorField],
            text: item[textoField]
          }));
          
          resolve(selectItems);
        })
        .catch(error => {
          reject(error);
        });
    });
  }
  
  /**
   * Recupera um item específico de uma tabela auxiliar pelo código
   * @param tabela Nome da tabela
   * @param codigo Código do item
   * @returns Promise com o item da tabela
   */
  public getItemTabela(tabela: string, codigo: number): Promise<TabelaAuxiliarItem> {
    return new Promise((resolve, reject) => {
      this.getListaTabela(tabela)
        .then(data => {
          const item = data.find(i => i.codigo === codigo);
          
          if (item) {
            resolve(item);
          } else {
            reject(`Item com código ${codigo} não encontrado na tabela ${tabela}`);
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /**
   * Sanitiza todos os campos de texto em um array de objetos
   * para prevenir ataques XSS
   * @param data Array de dados a ser sanitizado
   * @returns Array com os mesmos dados, mas com strings sanitizadas
   */
  private sanitizeStringProperties(data: any[]): TabelaAuxiliarItem[] {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(item => {
      const sanitizedItem: any = {};
      
      Object.keys(item).forEach(key => {
        if (typeof item[key] === 'string') {
          // Verifica se é um campo de opção de select para tratamento especial
          if (this.selectOptionFields.includes(key)) {
            sanitizedItem[key] = this.sanitizeSelectOptionText(item[key]);
          } else {
            sanitizedItem[key] = this.sanitizeHtml(item[key]);
          }
        } else {
          // Mantém outros tipos de dados inalterados
          sanitizedItem[key] = item[key];
        }
      });
      
      return sanitizedItem;
    });
  }
  
  /**
   * Sanitização específica para textos que aparecem em opções de select
   * @param str String a ser sanitizada
   * @returns String sanitizada preservando caracteres comuns como /
   */
  private sanitizeSelectOptionText(str: string): string {
    if (!str) return str;
    
    // Escapar apenas caracteres realmente perigosos para XSS
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }
  
  /**
   * Sanitização mais rigorosa para conteúdo HTML geral
   * @param str String a ser sanitizada
   * @returns String sanitizada
   */
  private sanitizeHtml(str: string): string {
    if (!str) return str;
    
    // Sanitização mais completa para HTML em geral
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;')
      .replace(/\\/g, '&#x5C;')
      .replace(/`/g, '&#x60;');
  }
  
  /**
   * Executa operação em lote para recuperar múltiplas tabelas de uma vez
   * @param tabelas Array com nomes das tabelas
   * @returns Promise com objeto contendo todas as tabelas solicitadas
   */
  public getMultipleTabelas(tabelas: string[]): Promise<Record<string, TabelaAuxiliarItem[]>> {
    return new Promise((resolve, reject) => {
      if (!tabelas || tabelas.length === 0) {
        resolve({});
        return;
      }
      
      const params = new HttpParams().set('tabelas', JSON.stringify(tabelas));
      
      this.http.get(`${this.urlBase}-multiple`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              const result: Record<string, TabelaAuxiliarItem[]> = {};
              
              // Sanitiza cada tabela retornada
              if (response.tabelas) {
                Object.keys(response.tabelas).forEach(tabela => {
                  result[tabela] = this.sanitizeStringProperties(response.tabelas[tabela] || []);
                });
              }
              
              resolve(result);
            } else {
              reject(response.message || 'Erro ao buscar múltiplas tabelas');
            }
          },
          error: (error) => {
            console.error('Erro ao buscar múltiplas tabelas');
            reject(error);
          }
        });
    });
  }
}


// WEBPACK FOOTER //
// /app/src/app/services/tabelasAuxiliares.service.ts